<script setup lang="ts">
import { parsePhoneNumber } from 'awesome-phonenumber';
import 'flag-icons/css/flag-icons.min.css';
import { COUNTRIES, COUNTRY_BY_CODE, type Country } from '~/constants/countries';
import { validatePhone, type PhoneValidationError } from '~/utils/phone/validatePhone';

const { t } = useI18n();

const {
  modelValue,
  valid: validProp,
  defaultCountry = 'AE',
  required = false,
  label = '',
  placeholder,
  id,
} = defineProps<{
  modelValue: string | null;
  valid?: boolean;
  defaultCountry?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  id?: string;
}>();

// Reactive fallback (a plain string prop default wouldn't re-translate on locale switch).
const effectivePlaceholder = computed(() => placeholder ?? t('common.phoneInput.placeholder'));

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
  (e: 'update:valid', valid: boolean): void;
}>();

// Unknown/unsupported codes (e.g. an unmapped IP-geo country) fall back to the UAE.
const FALLBACK_COUNTRY = 'AE';
const selectedCountryCode = ref<string>(COUNTRY_BY_CODE[defaultCountry] ? defaultCountry : FALLBACK_COUNTRY);
const nationalInput = ref<string>('');
const pickerOpen = ref(false);
const pickerQuery = ref('');
const pickerRef = ref<HTMLElement | null>(null);

const selectedCountry = computed<Country>(() => COUNTRY_BY_CODE[selectedCountryCode.value] ?? COUNTRIES[0]);

const filteredCountries = computed(() => {
  const q = pickerQuery.value.trim().toLowerCase();
  if (!q) return COUNTRIES;
  return COUNTRIES.filter(
    (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.code.toLowerCase().includes(q),
  );
});

const parsed = computed(() => {
  if (!nationalInput.value.trim()) return null;
  try {
    return parsePhoneNumber(nationalInput.value, { regionCode: selectedCountryCode.value });
  } catch {
    return null;
  }
});

// Shared with the server (utils/phone/validatePhone) so the UI rejects exactly
// what the signup/profile endpoints would: wrong length, invalid for the
// country, or throwaway patterns like 0000000 / 1234567.
const validation = computed(() => {
  if (!nationalInput.value.trim()) return null;
  // Prefer the parsed E.164 (handles trunk "0" prefixes); fall back to the raw
  // dial code + input so too-short/too-long still get a specific message.
  return validatePhone(parsed.value?.number?.e164 ?? `+${selectedCountry.value.dialCode}${nationalInput.value}`);
});

const isValid = computed(() => !!parsed.value?.valid && !!validation.value?.valid);

// validatePhone's `message` is English (shared with the server responses) — the
// UI shows the translated equivalent keyed by the stable error code.
const PHONE_ERROR_KEYS: Record<PhoneValidationError, string> = {
  missing: 'validation.phone.missing',
  tooShort: 'validation.phone.tooShort',
  tooLong: 'validation.phone.tooLong',
  fakePattern: 'validation.phone.fakePattern',
  invalid: 'validation.phone.invalid',
};

const errorMessage = computed(() => {
  const v = validation.value;
  if (v && !v.valid) return t(PHONE_ERROR_KEYS[v.error]);
  return t('common.phoneInput.invalid');
});

const emitState = () => {
  const e164 = parsed.value?.number?.e164 ?? null;
  emit('update:modelValue', e164);
  emit('update:valid', isValid.value);
};

watch([selectedCountryCode, nationalInput], emitState, { immediate: false });

// If parent resets modelValue (e.g. on form clear), sync back.
watch(
  () => modelValue,
  (next) => {
    if (!next) {
      if (nationalInput.value !== '') nationalInput.value = '';
      return;
    }
    if (parsed.value?.number?.e164 === next) return;
    try {
      const incoming = parsePhoneNumber(next);
      if (incoming?.valid && incoming.regionCode) {
        selectedCountryCode.value = incoming.regionCode;
        nationalInput.value = incoming.number.significant;
      }
    } catch {
      /* ignore */
    }
  },
  { immediate: true },
);

// Set once the user picks a dial code themselves — from then on defaultCountry never overrides it.
const countryPickedManually = ref(false);

// defaultCountry may resolve after mount (e.g. an IP-geo lookup, or the country-of-residence
// select). Apply it only while the user hasn't typed a number or picked a country; unknown codes
// keep the current selection.
watch(
  () => defaultCountry,
  (next) => {
    if (countryPickedManually.value || nationalInput.value.trim()) return;
    if (!next || !COUNTRY_BY_CODE[next]) return;
    selectedCountryCode.value = next;
  },
);

const handleCountryPicked = (code: string) => {
  countryPickedManually.value = true;
  selectedCountryCode.value = code;
  pickerOpen.value = false;
  pickerQuery.value = '';
};

const onDocumentClick = (event: MouseEvent) => {
  if (!pickerRef.value) return;
  if (!pickerRef.value.contains(event.target as Node)) {
    pickerOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
});

// Any typed-but-invalid number is flagged (optional fields must still be valid
// if filled in — the server rejects them otherwise).
const showError = computed(() => nationalInput.value.trim().length > 0 && !isValid.value);
</script>

<template>
  <div class="flex flex-col gap-1">
    <label
      v-if="label"
      :for="id"
      class="text-sm font-medium text-black-80"
    >
      {{ label
      }}<span
        v-if="required"
        class="text-red-500 ms-0.5"
        >*</span
      >
    </label>
    <div
      ref="pickerRef"
      class="relative flex h-10 w-full items-stretch rounded-lg border border-black-10 bg-white focus-within:border-black-40"
      :class="{ 'border-red-500 focus-within:border-red-500': showError }"
    >
      <!-- Dial code + national number ONLY: pin LTR so "+971 5X XXX XXXX" always reads
           correctly regardless of the page's direction. The dropdown below (country names,
           search placeholder) is a sibling, outside this pin, so it stays logical/localizable. -->
      <div
        class="flex flex-1 items-stretch"
        dir="ltr"
      >
        <button
          type="button"
          class="flex items-center gap-2 px-3 border-e border-black-10 hover:bg-black-05 rounded-s-lg text-sm"
          :aria-label="`Country code: ${selectedCountry.name}`"
          @click="pickerOpen = !pickerOpen"
        >
          <span
            class="fi inline-block w-5 h-3.5 shrink-0 rounded-sm"
            :class="`fi-${selectedCountry.code.toLowerCase()}`"
          ></span>
          <span class="font-medium">+{{ selectedCountry.dialCode }}</span>
          <UIIcon
            name="chevron-big-filled-down"
            size="xs"
          />
        </button>
        <input
          :id="id"
          v-model="nationalInput"
          type="tel"
          inputmode="tel"
          autocomplete="tel-national"
          :placeholder="effectivePlaceholder"
          class="flex-1 bg-transparent px-3 text-sm outline-none"
        />
      </div>
      <div
        v-if="pickerOpen"
        class="absolute start-0 top-full z-50 mt-1 flex w-full min-w-[280px] max-w-[360px] flex-col overflow-hidden rounded-lg border border-black-10 bg-white shadow-lg"
      >
        <div class="border-b border-black-10 p-2">
          <input
            v-model="pickerQuery"
            type="search"
            :placeholder="t('common.phoneInput.searchPlaceholder')"
            class="h-9 w-full rounded-md border border-black-10 bg-white px-2 text-sm outline-none focus:border-black-40"
          />
        </div>
        <div class="max-h-64 overflow-y-auto py-1">
          <button
            v-for="c in filteredCountries"
            :key="c.code"
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2 text-start text-sm hover:bg-black-05"
            :class="{ 'bg-black-05 font-medium': c.code === selectedCountryCode }"
            @click="handleCountryPicked(c.code)"
          >
            <span
              class="fi inline-block w-5 h-3.5 shrink-0 rounded-sm"
              :class="`fi-${c.code.toLowerCase()}`"
            ></span>
            <span class="flex-1 truncate">{{ c.name }}</span>
            <span class="text-black-60">+{{ c.dialCode }}</span>
          </button>
          <div
            v-if="!filteredCountries.length"
            class="px-3 py-4 text-center text-sm text-black-60"
          >
            {{ t('common.countryPicker.noMatches') }}
          </div>
        </div>
      </div>
    </div>
    <p
      v-if="showError"
      class="text-xs text-red-500"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
